import {
  CloseOutlined,
  LoadingOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button } from 'antd';
import { debounce, get, isFunction } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import config from '../../common/config';
import { DOCUMENT_CLICK, emitter } from '../../common/helpers/ui/EventHelper';
import { gotoSrp } from '../../common/helpers/routing/srpRoutesHelper';
import commonResource from '../../locales/th/common.json';
import { InputWrapper } from './DesktopSearchInput.style';
import SearchSuggestion from './SearchSuggestion';
import { trackFreeTextSearch } from '../../common/analytics/analyticsTracker';
import { useSearchSuggestions } from '../../hooks/useSearchSuggestions';
import { WholeState } from '../../store/common/type';

const SEARCH_TRIGGER_CHAR_LENGTH = 2;
const SEARCH_DEBOUNCE = 500;

interface DesktopSearchInputProps {
  updateSearchValue: (value: string) => void;
  updateSearchActive: (active: boolean) => void;
  currentSourcePage: string;
}
const DesktopSearchInput: React.FC<DesktopSearchInputProps> = ({
  updateSearchValue,
  updateSearchActive,
  currentSourcePage,
}) => {
  const [isRouteChanged, setIsRouteChanged] = useState(false);
  const searchInput = useRef(null);
  const client = useApolloClient();
  const searchInputPlaceholderText = commonResource.searchInputPlaceholder;
  const closeButtonText = commonResource.close;
  const userInfo = useSelector((state: WholeState) =>
    state !== undefined ? state.common.user : {},
  );

  // Use the hook
  const {
    initiateSearch,
    setShouldShowResult,
    isSearchInProgress,
    shouldShowResult,
    suggestions,
  } = useSearchSuggestions({ client });

  useEffect(() => {
    setTimeout(() => {
      searchInput.current.focus();
    });

    const handleDocumentClick = () => {
      setShouldShowResult(false);
    };

    if (config.isBrowser) {
      emitter.on(DOCUMENT_CLICK, handleDocumentClick);
    }

    return () => {
      if (config.isBrowser) {
        emitter.off(DOCUMENT_CLICK, handleDocumentClick);
      }
    };
  }, [setShouldShowResult]);

  const handleSearchValueChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    callback?: (value: string) => void,
  ) => {
    const searchValue = get(searchInput, 'current.value') || '';

    if (searchValue.length >= SEARCH_TRIGGER_CHAR_LENGTH) {
      initiateSearch(searchValue, isRouteChanged);
    } else {
      setShouldShowResult(false);
    }
    if (isFunction(callback)) callback(searchValue);
  };

  const debouncedSearchValueChange = debounce(
    handleSearchValueChange,
    SEARCH_DEBOUNCE,
  );

  const handleClickInput = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (isFunction(e.nativeEvent.stopImmediatePropagation)) {
      e.nativeEvent.stopImmediatePropagation();
    }
  };

  const handleClickClose = () => {
    updateSearchActive(false);
  };

  const searchFreeText = () => {
    setIsRouteChanged(true);
    const searchValue = get(searchInput, 'current.value');

    setTimeout(() => {
      if (!searchValue) {
        gotoSrp({});
      } else {
        gotoSrp({ q: searchValue });
      }
    }, 1000);
  };

  const handleSearchFreeText = () => {
    updateSearchActive(false);
    searchFreeText();
    trackFreeTextSearch(userInfo, currentSourcePage, searchInput.current.value);
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.code === 'Enter') handleSearchFreeText();
  };

  return (
    <InputWrapper onClick={handleClickInput}>
      {isSearchInProgress && (
        <LoadingOutlined onClick={() => handleSearchFreeText()} />
      )}
      {isSearchInProgress || (
        <SearchOutlined onClick={() => handleSearchFreeText()} />
      )}
      <input
        ref={searchInput}
        onFocus={debouncedSearchValueChange}
        onChange={(e) => debouncedSearchValueChange(e, updateSearchValue)}
        type="text"
        placeholder={searchInputPlaceholderText}
        onKeyUp={(e) => handleKeyUp(e)}
        data-test={'desktop-search-input'}
      />
      <Button className="btn-close-search" onClick={handleClickClose}>
        <CloseOutlined />
        {closeButtonText}
      </Button>

      <CSSTransition
        in={shouldShowResult}
        timeout={300}
        classNames="fade"
        unmountOnExit
      >
        <SearchSuggestion
          suggestions={suggestions}
          currentSourcePage={currentSourcePage}
        />
      </CSSTransition>
    </InputWrapper>
  );
};

export default DesktopSearchInput;
